import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import TaskChecklistCard from '../components/TaskChecklistCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Task, TaskChecklistItem, TaskPriority, TaskStatus, TaskType } from '../types/task';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface JobTask {
  id: string;
  task_number: string;
  task_type: TaskType;
  status: string;
}

interface TaskFormData {
  task_type: TaskType;
  task_order: number;
  assigned_to_user_id: string;
  status: TaskStatus;
  priority: TaskPriority;
  planned_start_at: string;
  planned_end_at: string;
  estimated_duration_hours: string;
  instructions: string;
  requires_photo: boolean;
  depends_on_task_ids: string[];
}

const EditTask: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [checklistItems, setChecklistItems] = useState<TaskChecklistItem[]>([]);
  const [checklistProgress, setChecklistProgress] = useState({ total: 0, completed: 0, percentage: 0 });
  const [jobTasks, setJobTasks] = useState<JobTask[]>([]);

  // Get navigation context
  const returnTo = (location.state as any)?.returnTo;
  const jobId = (location.state as any)?.jobId;
  const tab = (location.state as any)?.tab || 'overview';
  const [formData, setFormData] = useState<TaskFormData>({
    task_type: TaskType.DELIVERY,
    task_order: 1,
    assigned_to_user_id: '',
    status: TaskStatus.PENDING,
    priority: TaskPriority.NORMAL,
    planned_start_at: '',
    planned_end_at: '',
    estimated_duration_hours: '',
    instructions: '',
    requires_photo: false,
    depends_on_task_ids: [],
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadTaskData();
    loadUsers();
    loadChecklist();
  }, [id]);

  const loadTaskData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        const taskData = data.data;
        setTask(taskData);
        setFormData({
          task_type: taskData.task_type,
          task_order: taskData.task_order,
          assigned_to_user_id: taskData.assigned_to_user_id || '',
          status: taskData.status,
          priority: taskData.priority,
          planned_start_at: taskData.planned_start_at ? taskData.planned_start_at.split('.')[0] : '',
          planned_end_at: taskData.planned_end_at ? taskData.planned_end_at.split('.')[0] : '',
          estimated_duration_hours: taskData.estimated_duration_hours?.toString() || '',
          instructions: taskData.instructions || '',
          requires_photo: taskData.requires_photo || false,
          depends_on_task_ids: taskData.depends_on_task_ids || [],
        });
        // Load job tasks for dependency selection
        if (taskData.job_id) {
          loadJobTasks(taskData.job_id);
        }
      }
    } catch (err) {
      console.error('Error loading task:', err);
      alert('Failed to load task data');
      if (returnTo === 'job' && jobId) {
        navigate(`/jobs/${jobId}?tab=${tab}`);
      } else {
        navigate('/tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const usersList = Array.isArray(data) ? data : (data.users || data.data || []);
      setUsers(usersList);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadChecklist = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        const items = data.data || [];
        setChecklistItems(items);
        const total = items.length;
        const completed = items.filter((item: TaskChecklistItem) => item.is_completed).length;
        setChecklistProgress({
          total,
          completed,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        });
      }
    } catch (err) {
      console.error('Error loading checklist:', err);
    }
  };

  const loadJobTasks = async (taskJobId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/job/${taskJobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        // Filter out current task from the list
        const otherTasks = (data.data || []).filter((t: JobTask) => t.id !== id);
        setJobTasks(otherTasks);
      }
    } catch (err) {
      console.error('Error loading job tasks:', err);
    }
  };

  const handleDependencyToggle = (taskId: string, checked: boolean) => {
    setFormData(prev => {
      const current = prev.depends_on_task_ids || [];
      const updated = checked
        ? [...current, taskId]
        : current.filter(depId => depId !== taskId);
      return { ...prev, depends_on_task_ids: updated };
    });
  };

  const handleToggleChecklist = async (itemId: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist/${itemId}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadChecklist();
      }
    } catch (error) {
      console.error('Error toggling checklist:', error);
      alert('Failed to toggle checklist item');
    }
  };

  const handleAddChecklistItem = async (label: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label, item_order: checklistItems.length + 1 }),
      });
      const data = await response.json();
      if (data.success) {
        loadChecklist();
      } else {
        throw new Error(data.message || 'Failed to add checklist item');
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
      alert(error instanceof Error ? error.message : 'Failed to add checklist item');
      throw error;
    }
  };

  const handleDeleteChecklistItem = async (itemId: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${id}/checklist/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadChecklist();
      } else {
        throw new Error(data.message || 'Failed to delete checklist item');
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete checklist item');
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      const payload = {
        task_type: formData.task_type,
        task_order: formData.task_order,
        assigned_to_user_id: formData.assigned_to_user_id || null,
        status: formData.status,
        priority: formData.priority,
        planned_start_at: formData.planned_start_at || null,
        planned_end_at: formData.planned_end_at || null,
        estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : null,
        instructions: formData.instructions || null,
        requires_photo: formData.requires_photo,
        depends_on_task_ids: formData.depends_on_task_ids.length > 0 ? formData.depends_on_task_ids : null,
      };

      const response = await fetch(`/api/tasks/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update task');
      }

      navigate(`/tasks/${id}`, { state: { returnTo, jobId, tab } });
    } catch (error) {
      console.error('Error updating task:', error);
      alert(error instanceof Error ? error.message : 'Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading task...</div>
        </main>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Task not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/tasks/${id}`, { state: { returnTo, jobId, tab } })}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Task Details
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              Edit Task {task.task_number}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Task Type and Order */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="task_type">Task Type *</Label>
                  <Select
                    value={formData.task_type}
                    onValueChange={(value) => handleChange('task_type', value as TaskType)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TaskType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatTaskType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="task_order">Task Order *</Label>
                  <Input
                    id="task_order"
                    type="number"
                    min="1"
                    value={formData.task_order}
                    onChange={(e) => handleChange('task_order', parseInt(e.target.value))}
                    required
                  />
                </div>
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value as TaskStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TaskStatus.PENDING}>Pending</SelectItem>
                      <SelectItem value={TaskStatus.ASSIGNED}>Assigned</SelectItem>
                      <SelectItem value={TaskStatus.IN_PROGRESS}>In Progress</SelectItem>
                      <SelectItem value={TaskStatus.COMPLETED}>Completed</SelectItem>
                      <SelectItem value={TaskStatus.BLOCKED}>Blocked</SelectItem>
                      <SelectItem value={TaskStatus.CANCELLED}>Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => handleChange('priority', value as TaskPriority)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                      <SelectItem value={TaskPriority.NORMAL}>Normal</SelectItem>
                      <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                      <SelectItem value={TaskPriority.URGENT}>Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Assignment */}
              <div>
                <Label htmlFor="assigned_to_user_id">Assign To</Label>
                <Select
                  value={formData.assigned_to_user_id || 'unassigned'}
                  onValueChange={(value) => handleChange('assigned_to_user_id', value === 'unassigned' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Timing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="planned_start_at">Planned Start</Label>
                  <Input
                    id="planned_start_at"
                    type="datetime-local"
                    value={formData.planned_start_at}
                    onChange={(e) => handleChange('planned_start_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="planned_end_at">Planned End</Label>
                  <Input
                    id="planned_end_at"
                    type="datetime-local"
                    value={formData.planned_end_at}
                    onChange={(e) => handleChange('planned_end_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="estimated_duration_hours">Estimated Duration (hours)</Label>
                  <Input
                    id="estimated_duration_hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_duration_hours}
                    onChange={(e) => handleChange('estimated_duration_hours', e.target.value)}
                    placeholder="e.g., 2.5"
                  />
                </div>
              </div>

              {/* Instructions */}
              <div>
                <Label htmlFor="instructions">Instructions</Label>
                <Textarea
                  id="instructions"
                  value={formData.instructions || ''}
                  onChange={(e) => handleChange('instructions', e.target.value)}
                  placeholder="Enter task instructions..."
                  rows={4}
                />
              </div>

              {/* Requires Photo */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requires_photo"
                  checked={formData.requires_photo}
                  onChange={(e) => handleChange('requires_photo', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="requires_photo" className="cursor-pointer">
                  Requires photo evidence
                </Label>
              </div>

              {/* Dependencies Section */}
              {jobTasks.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-base font-semibold">Dependencies</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Select tasks that must be completed before this task
                  </p>
                  <div className="max-h-40 overflow-y-auto border rounded p-3 space-y-2">
                    {jobTasks.map((jt) => {
                      const isSelected = formData.depends_on_task_ids.includes(jt.id);
                      return (
                        <div key={jt.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`dep-${jt.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => handleDependencyToggle(jt.id, !!checked)}
                          />
                          <Label htmlFor={`dep-${jt.id}`} className="cursor-pointer text-sm flex-1">
                            {jt.task_number} - {formatTaskType(jt.task_type)}
                            <span className={`ml-1 text-xs ${jt.status === 'completed' ? 'text-green-600' : 'text-muted-foreground'}`}>
                              ({jt.status})
                            </span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                  {formData.depends_on_task_ids.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.depends_on_task_ids.length} dependency/ies selected
                    </p>
                  )}
                </div>
              )}

              {/* Checklist Section */}
              <div className="pt-4 border-t">
                <TaskChecklistCard
                  taskId={id || ''}
                  items={checklistItems}
                  progress={checklistProgress}
                  onToggle={handleToggleChecklist}
                  onAdd={handleAddChecklistItem}
                  onDelete={handleDeleteChecklistItem}
                  canEdit={true}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(`/tasks/${id}`)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Updating...' : 'Update Task'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EditTask;
